import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { BACKEND_URL } from "@/lib/api";

// Whitelist extendido para permitir imágenes con src="/api/files/..." o absolutas.
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "img"],
  attributes: {
    ...(defaultSchema.attributes || {}),
    img: ["src", "alt", "title", "width", "height"],
  },
  protocols: {
    ...(defaultSchema.protocols || {}),
    src: ["http", "https"],
  },
};

// Regex para @username · alfanumérico + . _ - · 2..64 chars
const MENTION_RE = /(?:(?<=^)|(?<=[\s(>]))@([a-zA-Z0-9._-]{2,64})/g;

function renderTextWithMentions(text) {
  if (typeof text !== "string" || !text.includes("@")) return text;
  const parts = [];
  let last = 0;
  let m;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span
        key={`m-${m.index}`}
        data-testid={`mention-${m[1]}`}
        className="inline-flex items-center px-1 rounded font-mono text-xs bg-brand-orange-500/15 text-brand-orange-500 border border-brand-orange-500/30"
      >
        @{m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : text;
}

/**
 * Fase 13.2 · Renderiza markdown con sanitización estricta.
 * Fase 14.5 · Permite <img> vía src (attachments) y resalta @menciones.
 */
export default function MarkdownContent({ children, className = "" }) {
  return (
    <div className={`markdown-content prose prose-invert prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
        components={{
          a: ({ node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" className="text-brand-orange-500 underline hover:text-brand-orange-500/80" />
          ),
          img: ({ node, src, alt, ...props }) => {
            let resolved = src || "";
            if (resolved && !resolved.startsWith("http")) {
              resolved = `${BACKEND_URL}${resolved}`;
            }
            return (
              <img
                {...props}
                src={resolved}
                alt={alt || ""}
                loading="lazy"
                className="max-w-full rounded-md border border-gray-800 my-2"
              />
            );
          },
          code: ({ inline, ...props }) =>
            inline ? (
              <code {...props} className="px-1 py-0.5 rounded bg-white/10 text-brand-orange-500 font-mono text-xs" />
            ) : (
              <pre className="p-3 rounded bg-black/40 border border-gray-800 overflow-x-auto text-xs">
                <code {...props} />
              </pre>
            ),
          // Renderizamos texto plano dentro de p, li, strong, em, etc con mentions
          p: ({ children, ...props }) => (
            <p {...props}>{React.Children.map(children, (c) =>
              typeof c === "string" ? renderTextWithMentions(c) : c
            )}</p>
          ),
          li: ({ children, ...props }) => (
            <li {...props}>{React.Children.map(children, (c) =>
              typeof c === "string" ? renderTextWithMentions(c) : c
            )}</li>
          ),
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
